import deepmerge from 'deepmerge'

import logger from '../common/log'

/**
 * A collection name, like <i>objects</i> or <i>drivers</i>,
 * which can be retrieved via a corresponding RESTful webservice.
 *
 * @typedef {string} RestCollection
 */

/**
 * @typedef {RestCollection[]} RestCollections
 */

/**
 * This type describes the data structure received from the Push Server.
 *
 * @typedef {Object<RestCollection, Object>} PushData
 */

/**
 * This class collects data received from the Push Server. You can add data by calling {@link collect},
 * retrieve by calling {@link get} or completely remove by calling {@link clear}.
 */
export class PushDataCollector {
    constructor() {
        this.data = {}
    }

    /**
     * Clears all the collected data.
     */
    clearAll() {
        if (this.hasData()) {
            logger.info('Discarding all collected push data.')
            this.data = {}
        }
    }

    /**
     * Clears all the collected data for the given collections.
     *
     * @param {RestCollections} collections
     */
    clear(collections = []) {
        collections.forEach(collection => {
            if (this.data[collection]) {
                logger.info(`Discarding collected push data for '${collection}'.`)
                delete this.data[collection]
            }
        })
    }

    /**
     * Returns <code>true</code>, if there was something collected, otherwise <code>false</code>.
     * @return {boolean} true / false.
     */
    hasData() {
        return Object.keys(this.data).length > 0
    }

    /**
     * Collects the data received from the Push Server by adding it to an object.
     * @param {PushData} data
     */
    collect(data) {
        if (typeof data === 'string') {
            this.aggregateString(data)
        } else if (typeof data === 'object') {
            this.aggregateObject(data)
        } else {
            logger.error('Illegal data type: ' + typeof data)
        }
    }

    /**
     * @private
     * @param {string} data
     */
    aggregateString(data) {
        this.aggregateObject(JSON.parse(data))
    }

    /**
     * @private
     * @param {Object} data
     */
    aggregateObject(data) {
        const overwriteMerge = (destination, source, options) => source
        this.data = deepmerge(this.data, data, { arrayMerge: overwriteMerge })
    }

    /**
     * Returns all the data received until this moment from the Push Server.
     * @returns {PushData}
     */
    get() {
        return this.data
    }
}
